import { Checkbox, InlineNotification, Modal } from '@carbon/react';
import { format0 } from 'utils/number';
import { useAtom } from 'jotai';
import { formatMutationResource } from 'utils/misc';
import { LandingAnalysisDefaultParametersResponse } from 'types/api-responses';
import { enableAutoUpdateAtom } from '../atoms';
import styles from './auto-update-modal.module.scss';

interface AutoUpdateModalProps {
  isOpen: boolean;
  collectionId: string;
  collectionName: string;
  onClose: () => void;
  onConfirm: () => void;
  landingParameters?: LandingAnalysisDefaultParametersResponse['landingParameters'];
  hasParametersError?: boolean;
}

export const AutoUpdateModal = ({
  isOpen,
  collectionId,
  collectionName,
  onClose,
  onConfirm,
  landingParameters,
  hasParametersError,
}: AutoUpdateModalProps) => {
  const [enableAutoUpdateState, enableAutoUpdate] =
    useAtom(enableAutoUpdateAtom);
  const { loading: isSubmitting } = formatMutationResource(
    enableAutoUpdateState
  );
  const hasSubmitError =
    enableAutoUpdateState.result?.type === 'error' ||
    enableAutoUpdateState.result?.type === 'fatal';

  const handleConfirm = () => {
    enableAutoUpdate({
      url: `/structure_grid_collections/${collectionId}`,
      type: 'PATCH',
      data: { landingAutoUpdateEnabled: true },
      onSuccess: onConfirm,
    });
  };

  return (
    <Modal
      open={isOpen}
      modalLabel={collectionName}
      modalHeading="Enable Auto Update for Landing Analysis"
      primaryButtonText={isSubmitting ? 'Enabling...' : 'Enable Auto Update'}
      primaryButtonDisabled={isSubmitting}
      secondaryButtonText="Cancel"
      onRequestSubmit={handleConfirm}
      onRequestClose={isSubmitting ? undefined : onClose}
      className={styles.modal}
    >
      {hasSubmitError && (
        <InlineNotification
          kind="error"
          title="Failed to enable Auto Update."
          subtitle="Please try again."
          hideCloseButton
          lowContrast
        />
      )}
      {hasParametersError && (
        <InlineNotification
          kind="error"
          title="Failed to load landing parameters."
          subtitle="Parameter values are unavailable. Please close and try again."
          hideCloseButton
          lowContrast
        />
      )}

      <div className={styles.body}>
        <p className={styles.description}>
          When Auto Update is enabled, landing analysis will run automatically
          for this collection whenever new well geometries are detected.
        </p>
        <p className={styles.description}>
          These updates may persist new landing analysis results without manual
          confirmation. You can disable Auto Update at any time from the Landing
          Analysis section.
        </p>

        <div className={styles.fields}>
          <div className={styles.fieldFull}>
            <span className={styles.fieldLabel}>Structure Grid Collection</span>
            <span className={styles.fieldValue}>{collectionName}</span>
          </div>

          <div className={styles.fieldGrid}>
            <div className={styles.fieldItem}>
              <span className={styles.fieldLabel}>Inclination Cutoff</span>
              <span className={styles.fieldValue}>
                {landingParameters?.horizontalInclinationCutoffDeg ?? '—'}
              </span>
            </div>

            <div className={styles.fieldItem}>
              <span className={styles.fieldLabel}>
                Well Segment Step Frequency
              </span>
              <span className={styles.fieldValue}>
                {landingParameters?.stepLengthFt ?? '—'}
              </span>
            </div>

            <div className={styles.fieldItem}>
              <span className={styles.fieldLabel}>Maximum Thickness</span>
              <span className={styles.fieldValue}>
                {landingParameters?.thicknessCutoffFt !== null
                  ? `${format0(landingParameters?.thicknessCutoffFt)} ft`
                  : '—'}
              </span>
            </div>

            <div className={styles.fieldItem}>
              <Checkbox
                id="auto-update-use-perforation-geometry"
                labelText="Use Perforation Geometry"
                checked={landingParameters?.usePerforations ?? false}
                disabled
                onChange={() => {}}
                className={styles.checkbox}
              />
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};
